/**
 * Quem conversa com quem, depois dos canais (A146).
 *
 * Até a A145 isto era um catálogo de 36 pares ordenados (A030), com
 * interruptores e alcances que cada marca configurava na "Política de chat".
 * Os canais (A144) tornaram a matriz desnecessária: a marca escolhe quais
 * chats quer ter e quem atende cada um. Sobrou uma regra só, que nenhuma
 * marca configura:
 *
 * - **a equipe da marca conversa entre si** — administração, relacionamento,
 *   conteúdo e profissionais, inclusive quem chega por concessão (a rede de
 *   profissionais, o time de relacionamento de outra marca);
 * - **paciente e gestora falam só pelos canais.** Ninguém abre conversa direta
 *   com elas, e elas não abrem com ninguém: falar com paciente é atender um
 *   canal que serve a ela.
 *
 * `MAINTAINER` e `GUEST` continuam sem papel de chat.
 */
import { z } from 'zod';
import type { BrandRole } from './membership.schema';

export const PAPEIS_DE_CHAT = [
  'BRAND_ADMIN',
  'BRAND_CRM',
  'BRAND_CMS',
  'PROVIDER',
  'COMPANY_MANAGER',
  'PATIENT',
] as const;

export type PapelDeChat = (typeof PAPEIS_DE_CHAT)[number];

/** `null` para quem não conversa: mantenedora e acompanhante. */
export function papelDeChat(role: BrandRole): PapelDeChat | null {
  return (PAPEIS_DE_CHAT as readonly string[]).includes(role)
    ? (role as PapelDeChat)
    : null;
}

/** Os papéis da equipe: os que conversam entre si e podem atender um canal. */
export const PAPEIS_DA_EQUIPE: readonly PapelDeChat[] = ['BRAND_ADMIN', 'BRAND_CRM', 'BRAND_CMS', 'PROVIDER'];

/**
 * A pessoa é da equipe nesta marca? Basta um papel: quem é administradora e
 * também paciente da mesma marca conversa com a equipe como administradora.
 */
export function ehDaEquipe(papeis: readonly PapelDeChat[]): boolean {
  return papeis.some((p) => PAPEIS_DA_EQUIPE.includes(p));
}

/**
 * Conversa direta (ou grupo fora de canal) entre estas pessoas pode ser
 * aberta? Só se todas forem da equipe da marca.
 */
export function podeAbrirConversaDireta(lados: ReadonlyArray<readonly PapelDeChat[]>): boolean {
  return lados.length > 0 && lados.every(ehDaEquipe);
}

/**
 * O que a marca tinha mudado na matriz antiga (A030). **Legado**: ninguém lê
 * nem grava desde a A146. Continua no schema da marca porque há marcas com o
 * campo gravado, e `BaseRepository` faz `parse` em toda leitura — tirar daqui
 * antes de apagar o campo do disco quebraria a leitura delas.
 */
export const ChatPolicySchema = z.object({
  pares: z.record(z.boolean()).optional(),
  alcances: z.record(z.enum(['vinculo', 'empresa', 'marca'])).optional(),
  alteradoPor: z.string().nullable().optional(),
  alteradoEm: z.date().nullable().optional(),
});

export type ChatPolicy = z.infer<typeof ChatPolicySchema>;
